import {
  IMPACT_LEVEL_LABELS,
  INCIDENT_SEVERITY_LABELS,
  ImpactLevel,
  IncidentSeverity,
  PermissionCode,
  TICKET_PRIORITY_LABELS,
  TicketType,
  URGENCY_LEVEL_LABELS,
  resolveIncidentPriority,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import {
  MajorIncidentBadge,
  PriorityBadge,
} from '@/products/helpdesk/features/tickets/ticket-badges';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import {
  useBroadcastIncident,
  useLinkMajorIncidentChild,
  useUnlinkMajorIncidentChild,
  useUpdateIncident,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';

export function TicketIncidentPanel({ ticket }: { ticket: TicketDetail }) {
  const copy = useVoiceCopy();
  const incident = ticket.incident;
  const me = useMe();
  const paths = useAppPaths();
  const canUpdate = hasPermission(me.data, PermissionCode.TICKETS_UPDATE);
  const canBroadcast = hasPermission(me.data, PermissionCode.TICKETS_COMMENT);
  const editable = canUpdate && !paths.isPortal;
  const updateIncident = useUpdateIncident(String(ticket.number));
  const linkChild = useLinkMajorIncidentChild(String(ticket.number));
  const unlinkChild = useUnlinkMajorIncidentChild(String(ticket.number));
  const broadcast = useBroadcastIncident(String(ticket.number));

  const [severity, setSeverity] = useState<IncidentSeverity>(
    incident?.severity ?? IncidentSeverity.MINOR,
  );
  const [impact, setImpact] = useState<ImpactLevel>(incident?.impact ?? ImpactLevel.MEDIUM);
  const [urgency, setUrgency] = useState<ImpactLevel>(incident?.urgency ?? ImpactLevel.MEDIUM);
  const [childNumber, setChildNumber] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');

  if (!incident) {
    return null;
  }

  const isIncidentType = ticket.type === TicketType.INCIDENT;
  if (!isIncidentType) {
    if (!incident.lead) {
      return null;
    }
    return (
      <TicketSectionCard
        tone="red"
        collapsible
        defaultOpen={false}
        title="Major Incident"
        description="Dieses Ticket ist Teil eines gebündelten Major Incidents."
      >
        <p className="text-sm">
          <span className="text-muted-foreground">Leit-Incident: </span>
          <Link
            to={`${paths.tickets}/${incident.lead.number}`}
            className="text-primary hover:underline"
          >
            #{incident.lead.number} · {incident.lead.subject}
          </Link>
        </p>
      </TicketSectionCard>
    );
  }

  const preview = resolveIncidentPriority({ impact, urgency, severity });

  async function onSave() {
    try {
      await updateIncident.mutateAsync({ impact, urgency, severity });
      toast.success('Incident-Bewertung gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onLink() {
    const parsed = Number(childNumber.replace(/^#/, '').trim());
    if (!Number.isInteger(parsed) || parsed < 1) {
      toast.error(copy('helpdesk.ticket.number.enter'));
      return;
    }
    try {
      await linkChild.mutateAsync({ ticketNumber: parsed });
      toast.success(`Ticket #${parsed} verknüpft.`);
      setChildNumber('');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verknüpfung fehlgeschlagen.');
    }
  }

  async function onUnlink(childId: string) {
    try {
      await unlinkChild.mutateAsync(childId);
      toast.success('Verknüpfung entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Lösen fehlgeschlagen.');
    }
  }

  async function onBroadcast() {
    try {
      await broadcast.mutateAsync({ body: broadcastBody });
      toast.success('Mitteilung an alle Betroffenen gesendet.');
      setBroadcastBody('');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Senden fehlgeschlagen.');
    }
  }

  return (
    <TicketSectionCard
      tone="red"
      collapsible
      defaultOpen={false}
      toggleLabel="Incident"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Incident
          {incident.isMajor && <MajorIncidentBadge />}
        </span>
      }
      description={
        <>
          Impact und Urgency bestimmen die Priorität automatisch.
          {incident.isMajor ? ' Dieses Ticket ist der Leit-Incident des Bündels.' : null}
        </>
      }
      contentClassName="space-y-4"
    >
      {incident.lead && (
        <p className="text-sm">
          <span className="text-muted-foreground">Teil von Major Incident: </span>
          <Link
            to={`${paths.tickets}/${incident.lead.number}`}
            className="text-primary hover:underline"
          >
            #{incident.lead.number} · {incident.lead.subject}
          </Link>
        </p>
      )}
      {incident.problem && (
        <p className="text-sm">
          <span className="text-muted-foreground">Zugehöriges Problem: </span>
          <Link
            to={`${paths.tickets}/${incident.problem.number}`}
            className="text-primary hover:underline"
          >
            #{incident.problem.number} · {incident.problem.subject}
          </Link>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="incident-impact">Impact</Label>
          <Select
            value={impact}
            onValueChange={(value) => setImpact(value as ImpactLevel)}
            disabled={!editable}
          >
            <SelectTrigger id="incident-impact" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ImpactLevel).map((code) => (
                <SelectItem key={code} value={code}>
                  {IMPACT_LEVEL_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="incident-urgency">Urgency</Label>
          <Select
            value={urgency}
            onValueChange={(value) => setUrgency(value as ImpactLevel)}
            disabled={!editable}
          >
            <SelectTrigger id="incident-urgency" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ImpactLevel).map((code) => (
                <SelectItem key={code} value={code}>
                  {URGENCY_LEVEL_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(!paths.isPortal || incident.severity === IncidentSeverity.MAJOR) && (
          <div className="space-y-1.5">
            <Label htmlFor="incident-severity">Schweregrad</Label>
            <Select
              value={severity}
              onValueChange={(value) => setSeverity(value as IncidentSeverity)}
              disabled={!editable}
            >
              <SelectTrigger id="incident-severity" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(IncidentSeverity).map((code) => (
                  <SelectItem key={code} value={code}>
                    {INCIDENT_SEVERITY_LABELS[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Ermittelte Priorität</Label>
          <div className="flex h-9 items-center">
            <PriorityBadge priority={preview.priority} />
            <span className="ml-2 text-xs text-muted-foreground">
              {TICKET_PRIORITY_LABELS[preview.priority]}
            </span>
          </div>
        </div>
      </div>

      {editable && (
        <>
          <Button type="button" onClick={() => void onSave()} disabled={updateIncident.isPending}>
            {updateIncident.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            Bewertung speichern
          </Button>
          {severity === IncidentSeverity.MAJOR && !incident.isMajor && (
            <p className="text-xs text-muted-foreground">
              {copy('helpdesk.incident.major-bundle')}
            </p>
          )}
        </>
      )}

      {incident.isMajor && (
        <div className="space-y-3 border-t pt-4">
          <h3 className="text-sm font-medium">Verknüpfte Tickets</h3>
          {incident.children.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Tickets gebündelt.</p>
          ) : (
            <ul className="space-y-2">
              {incident.children.map((child) => (
                <li key={child.id} className="flex items-start justify-between gap-2 text-sm">
                  <Link
                    to={`${paths.tickets}/${child.number}`}
                    className="text-primary hover:underline"
                  >
                    #{child.number} · {child.subject}
                    {child.requester ? ` · ${child.requester.displayName}` : ''}
                  </Link>
                  {editable && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={unlinkChild.isPending}
                      onClick={() => void onUnlink(child.id)}
                    >
                      Lösen
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {editable && (
            <div className="flex gap-2">
              <Input
                value={childNumber}
                onChange={(event) => setChildNumber(event.target.value)}
                placeholder="Ticketnummer, z. B. 128"
                inputMode="numeric"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => void onLink()}
                disabled={linkChild.isPending}
              >
                {linkChild.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                Bündeln
              </Button>
            </div>
          )}
        </div>
      )}

      {incident.isMajor && canBroadcast && !paths.isPortal && (
        <div className="space-y-2 border-t pt-4">
          <Label htmlFor="incident-broadcast">Mitteilung an alle Betroffenen</Label>
          <textarea
            id="incident-broadcast"
            rows={4}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            placeholder="Status-Update, das auf dem Leit-Incident und allen verknüpften Tickets erscheint …"
            value={broadcastBody}
            onChange={(event) => setBroadcastBody(event.target.value)}
          />
          {incident.broadcastNote && (
            <p className="text-xs text-muted-foreground">
              Letzte Mitteilung: {incident.broadcastNote.replace(/<[^>]+>/g, ' ').trim()}
            </p>
          )}
          <Button type="button" onClick={() => void onBroadcast()} disabled={broadcast.isPending}>
            {broadcast.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            An alle senden
          </Button>
        </div>
      )}
    </TicketSectionCard>
  );
}
